import type { StorefrontCountriesQuery } from '#graphql-operations'
import type { StorefrontCountryPreference } from '../utils/countrySelector'
import { useQuery } from '@pinia/colada'
import { currentMarketCountryCode, isCurrentMarketPath, parseStorefrontCountryPreference } from '../utils/countrySelector'

type StorefrontCountries = StorefrontCountriesQuery['storefrontCountries']

async function fetchStorefrontCountries(): Promise<StorefrontCountries> {
  const result = await useGraphqlQuery('storefrontCountries')
  if (!result.data?.storefrontCountries)
    throw new Error(result.errors?.[0]?.message || 'Could not load storefront countries')
  return result.data.storefrontCountries
}

/**
 * The backend's country list for the current locale, shared by the selector and
 * by `useStorefrontCountry`. Never fetched on its own: callers refresh it on
 * intent. A short freshness window reuses the list while still discovering new
 * markets without a deployment.
 */
export function useStorefrontCountriesQuery() {
  const { locale } = useI18n()
  return useQuery({
    key: () => ['storefront-countries', locale.value],
    query: fetchStorefrontCountries,
    enabled: false,
    staleTime: 60_000,
  })
}

/**
 * The destination country the shopper picked in the country selector, for
 * everything that defaults a destination: free-delivery progress, the
 * checkout's initial country, payment-method filtering. `countryCode` is that
 * choice where it belongs to the current market, else the locale's country.
 *
 * Applied only once the page has hydrated, and never on the server: catalog
 * HTML is edge-cached for every visitor and the cookie is not part of the cache
 * key, so a server render that read it would hand one shopper's country to the
 * next. The state stays `undefined` (not yet read) until then, and every
 * consumer is a computed or a client-side effect that follows it afterwards.
 */
export function useStorefrontCountry() {
  const { locale } = useI18n()
  const localeObject = useLocaleObject()
  const nuxtApp = useNuxtApp()
  const countriesQuery = useStorefrontCountriesQuery()
  const preference = useState<StorefrontCountryPreference | null | undefined>('storefront-country:preference', () => undefined)
  // Bumped by an explicit pick only, so an order's seeded destination can follow
  // the shopper's choice without reacting to the cookie being restored.
  const selections = useState('storefront-country:selections', () => 0)
  const cookie = import.meta.server
    ? undefined
    : useCookie<StorefrontCountryPreference | null>('storefront-country', {
        default: () => null,
        maxAge: 60 * 60 * 24 * 365,
        sameSite: 'lax',
      })

  function restore() {
    if (preference.value === undefined)
      preference.value = parseStorefrontCountryPreference(cookie?.value)
  }

  if (!import.meta.server && preference.value === undefined) {
    if (nuxtApp.isHydrating)
      nuxtApp.hooks.hookOnce('app:suspense:resolve', restore)
    else
      restore()
  }

  const origin = () => import.meta.server ? '' : window.location.origin

  // Only a saved country other than the market default needs the list to be
  // confirmed, so a shopper who never picked one (or picked the default) costs
  // no request.
  const needsCountries = computed(() => {
    const saved = preference.value
    return !!saved
      && saved.code !== localeObject.value.country.toUpperCase()
      && isCurrentMarketPath(saved.path, { origin: origin(), locale: locale.value })
  })

  if (!import.meta.server) {
    watch(needsCountries, (needed) => {
      if (needed && !countriesQuery.data.value)
        void countriesQuery.refresh()
    }, { immediate: true })
  }

  const savedCountryCode = computed(() => import.meta.server
    ? null
    : currentMarketCountryCode(preference.value, {
        origin: origin(),
        locale: locale.value,
        defaultCountry: localeObject.value.country,
        countries: countriesQuery.data.value,
      }))

  const countryCode = computed(() => savedCountryCode.value ?? localeObject.value.country)

  /**
   * `countryCode` for a one-off read in client code (seeding an order): waits
   * for hydration and for the country list, so it never settles on the
   * fallback just because the confirmation was still in flight.
   */
  async function resolveCountryCode(): Promise<string> {
    if (!import.meta.server) {
      if (nuxtApp.isHydrating)
        await new Promise<void>(resolve => nuxtApp.hooks.hookOnce('app:suspense:resolve', () => resolve()))
      restore()
      if (needsCountries.value && !countriesQuery.data.value)
        await countriesQuery.refresh()
    }
    return countryCode.value
  }

  /** Records a pick from the selector: the cookie for later visits, the state for this one. */
  function choose(code: string, path: string) {
    const value = { code, path }
    if (cookie)
      cookie.value = value
    preference.value = value
    selections.value++
  }

  return {
    countryCode,
    savedCountryCode,
    selections: computed(() => selections.value),
    resolveCountryCode,
    choose,
  }
}
